import {defineSlotRecipe} from '@chakra-ui/react3'

/**
 * Recipe for Accordion Module
 * Based on Figma designs: https://www.figma.com/design/54EbshuMlvuqCBKFYoAhHg/STOKKE-Design-System?node-id=820-13525
 *
 * Slots: container, heading, accordion, item, trigger, itemHeadline, indicator, content, contentInner, textContent, cta, assetWrapper
 * Variants:
 *  - alignment: left | center
 */

export default defineSlotRecipe({
    slots: [
        'container',
        'heading',
        'accordion',
        'item',
        'trigger',
        'itemHeadline',
        'indicator',
        'content',
        'contentInner',
        'textContent',
        'cta',
        'assetWrapper',
        'videoDarkenLayer',
        'videoControl'
    ],
    base: {
        container: {
            // Base container styling from ModuleContainer
            px: {base: 'spacing-24', s: 'spacing-90'},
            paddingBlockStart: {base: 'spacing-64', s: 'spacing-128'},
            paddingBlockEnd: {base: 'spacing-40', s: 'spacing-128'}
        },
        accordion: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        item: {
            borderBottom: '1px solid',
            borderColor: 'border.medium'
        },
        trigger: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            py: 'spacing-24',
            px: 0
        },
        itemHeadline: {
            textStyle: 'heading.large',
            color: 'text.primary',
            textAlign: 'start'
        },
        indicator: {
            color: 'icons.primary',
            flexShrink: 0,
            _icon: {
                width: 5,
                height: 5
            }
        },
        content: {
            // need 'visible' to allow the focus of button/link to go outside the content
            overflow: 'visible'
        },
        contentInner: {
            pb: 'spacing-32',
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-24'
        },
        textContent: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-24'
        },
        cta: {
            alignSelf: 'flex-start'
        },
        assetWrapper: {
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            borderRadius: 'large'
        },
        videoDarkenLayer: {
            position: 'absolute',
            insetBlockStart: '50%',
            insetBlockEnd: 0,
            insetInline: 0,
            bg: 'gradient.bottom.darken-80'
        },
        videoControl: {
            // center in parent
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%);'
        }
    },
    variants: {
        alignment: {
            left: {
                container: {
                    display: 'flex',
                    flexDirection: {base: 'column', s: 'row'},
                    gap: {base: 'spacing-40', s: '0'},
                    alignItems: 'flex-start',
                    justifyContent: {s: 'space-between'}
                },
                heading: {
                    // Sticky heading at desktop
                    position: {s: 'sticky'},
                    top: 'var(--header-height, 0px)',
                    // Desktop: additional padding from Figma
                    paddingRight: {s: 'accordion-left-heading-padding-right'},
                    maxW: {s: 'accordion-left-heading-max-width'}
                },
                accordion: {
                    // Desktop: from Figma
                    maxW: {s: 'accordion-left-content-max-width'}
                }
            },
            center: {
                container: {
                    display: 'flex',
                    flexDirection: 'column',
                    // Desktop: gap-64, Mobile: gap-40
                    gap: {base: 'spacing-40', s: 'spacing-64'},
                    alignItems: {base: 'stretch', s: 'center'}
                },
                heading: {
                    alignSelf: 'center',
                    textAlign: 'center',
                    // Desktop: from Figma
                    maxW: {s: 'accordion-center-heading-max-width'}
                },
                accordion: {
                    // Desktop: from Figma
                    maxW: {s: 'accordion-center-content-max-width'}
                }
            }
        }
    },
    defaultVariants: {
        alignment: 'left'
    }
})
